import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { createResource, listResource, toggleResourceStatus, updateResource } from '../../../api/cabsAdmin';
import { renderMarkdown } from '../../../utils/markdown';

const normalizeValue = (type, value) => {
  if (type === 'number') return value === '' ? '' : Number(value);
  if (type === 'checkbox') return Boolean(value);
  return value;
};

const CabCrudPage = ({
  title,
  description,
  resource,
  columns,
  fields,
  defaultItem,
  transformPayload,
  rowActions,
  listParams,
  listParamsDeps,
  filterControls,
  mapItemToForm,
  uploadHandler,
  enableReorder,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultItem);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [orderedItems, setOrderedItems] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [reorderDirty, setReorderDirty] = useState(false);
  const [reorderSaving, setReorderSaving] = useState(false);
  const markdownRefs = useRef({});

  const requiredFields = useMemo(() => fields.filter((f) => f.required), [fields]);

  const resolveListParams = () => (typeof listParams === 'function' ? listParams() : listParams || {});

  const load = async (page = pagination.page) => {
    setLoading(true);
    setError('');
    try {
      const res = await listResource(resource, {
        q: query,
        isActive: statusFilter || undefined,
        page,
        limit: pagination.limit,
        ...resolveListParams(),
      });
      const data = res?.data || res || [];
      setItems(data);
      if (res?.pagination) {
        setPagination(res.pagination);
      } else {
        setPagination((prev) => ({ ...prev, page, totalPages: 1, total: data.length }));
      }
    } catch (err) {
      setError(err.message || 'Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enableReorder) return;
    setOrderedItems(items);
    setReorderDirty(false);
  }, [items, enableReorder]);

  useEffect(() => {
    if (!listParamsDeps) return undefined;
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, listParamsDeps || []);

  const openCreate = () => {
    setEditing(null);
    setForm(defaultItem);
    setShowModal(true);
  };

  const handleUpload = async (fieldName, file) => {
    if (!uploadHandler || !file) return;
    setUploading((prev) => ({ ...prev, [fieldName]: true }));
    try {
      const res = await uploadHandler(file);
      const url = res?.url || res?.path || '';
      if (url) {
        setForm((prev) => ({ ...prev, [fieldName]: url }));
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleDragStart = (event, index) => {
    if (event?.dataTransfer) {
      event.dataTransfer.setData('text/plain', String(index));
    }
    setDragIndex(index);
  };

  const handleDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return;
    const next = [...orderedItems];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    setOrderedItems(next);
    setReorderDirty(true);
    setDragIndex(null);
  };

  const insertMarkdown = (fieldName, before, after, placeholder, currentValue) => {
    const textarea = markdownRefs.current[fieldName];
    if (!textarea) return;
    const start = textarea.selectionStart ?? currentValue.length;
    const end = textarea.selectionEnd ?? currentValue.length;
    const selected = currentValue.slice(start, end) || placeholder;
    const next =
      currentValue.slice(0, start) + before + selected + after + currentValue.slice(end);
    setForm((prev) => ({ ...prev, [fieldName]: next }));
    requestAnimationFrame(() => {
      textarea.focus();
      const cursorStart = start + before.length;
      const cursorEnd = cursorStart + selected.length;
      textarea.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const saveOrder = async () => {
    if (!enableReorder || !reorderDirty) return;
    setReorderSaving(true);
    setError('');
    try {
      for (let idx = 0; idx < orderedItems.length; idx += 1) {
        const item = orderedItems[idx];
        await updateResource(resource, item.id, { ...item, sortOrder: idx + 1 });
      }
      await load(1);
      setReorderDirty(false);
    } catch (err) {
      setError(err.message || 'Failed to save order');
    } finally {
      setReorderSaving(false);
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm(mapItemToForm ? mapItemToForm(item, defaultItem) : { ...defaultItem, ...item });
    setShowModal(true);
  };

  const handleSave = async () => {
    const missing = requiredFields.find((field) => {
      const value = form[field.name];
      if (field.type === 'checkbox') return false;
      return value === undefined || value === null || value === '';
    });
    if (missing) {
      setError(`${missing.label || missing.name} is required.`);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = transformPayload ? transformPayload(form) : form;
      if (editing?.id) {
        await updateResource(resource, editing.id, payload);
      } else {
        await createResource(resource, payload);
      }
      setShowModal(false);
      await load(1);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (item) => {
    setError('');
    try {
      await toggleResourceStatus(resource, item.id, !item.isActive);
      await load(pagination.page);
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600">Cab Services</p>
          <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-600">{description}</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          Add new
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="row g-3">
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load(1)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="col-md-3">
            <button className="btn btn-outline-primary w-100" onClick={() => load(1)}>
              Apply filters
            </button>
          </div>
        </div>
        {filterControls ? <div className="mt-3">{filterControls}</div> : null}

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          {enableReorder && (
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={!reorderDirty || reorderSaving}
              onClick={saveOrder}
            >
              {reorderSaving ? 'Saving order...' : 'Save order'}
            </button>
          )}
        </div>

        <div className="table-responsive border rounded-lg">
          <table className="table table-sm mb-0 align-middle">
            <thead className="table-light">
              <tr>
                {enableReorder && <th style={{ width: 90 }}>Order</th>}
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={columns.length + (enableReorder ? 3 : 2)} className="text-center text-muted">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={columns.length + (enableReorder ? 3 : 2)} className="text-center text-muted">
                    No records yet
                  </td>
                </tr>
              )}
              {(enableReorder ? orderedItems : items).map((item, index) => (
                <tr
                  key={item.id}
                  onDragOver={(e) => enableReorder && e.preventDefault()}
                  onDrop={() => handleDrop(index)}
                >
                  {enableReorder && (
                    <td className="text-muted">
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-light text-dark border">{index + 1}</span>
                        <button
                          className="btn btn-sm btn-outline-secondary px-2"
                          draggable
                          onDragStart={(event) => handleDragStart(event, index)}
                          title="Drag to reorder"
                          type="button"
                        >
                          ≡
                        </button>
                      </div>
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(item) : item[col.key] ?? '-'}
                    </td>
                  ))}
                  <td>
                    <span className={`badge ${item.isActive ? 'bg-success-subtle text-success' : 'bg-secondary'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-end">
                    <div className="btn-group">
                      {rowActions ? rowActions(item, { openEdit }) : null}
                      <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(item)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleStatus(item)}>
                        {item.isActive ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 text-sm text-slate-600">
        <span>
          Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
        </span>
        <div className="btn-group">
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={pagination.page <= 1}
            onClick={() => load(Math.max(1, pagination.page - 1))}
          >
            Prev
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => load(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit' : 'Create'} {title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
              {fields.map((field) => {
                const value = form[field.name] ?? (field.type === 'checkbox' ? false : '');
                const options = typeof field.options === 'function' ? field.options() : field.options;
                return (
                  <div className={field.col || 'col-md-6'} key={field.name}>
                    <label className="form-label text-sm">{field.label}</label>
                  {field.type === 'textarea' ? (
                    <textarea
                      className="form-control"
                      value={value}
                      onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.value }))}
                      rows={field.rows || 3}
                    />
                  ) : field.type === 'markdown' ? (
                    <div className="d-flex flex-column gap-2">
                      <div className="btn-group" role="group" aria-label="Markdown toolbar">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          type="button"
                          onClick={() => insertMarkdown(field.name, '**', '**', 'bold text', value)}
                        >
                          Bold
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          type="button"
                          onClick={() => insertMarkdown(field.name, '*', '*', 'italic text', value)}
                        >
                          Italic
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          type="button"
                          onClick={() => insertMarkdown(field.name, '[', '](url)', 'link text', value)}
                        >
                          Link
                        </button>
                      </div>
                      <textarea
                        className="form-control"
                        value={value}
                        onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        rows={field.rows || 6}
                        ref={(node) => {
                          if (node) markdownRefs.current[field.name] = node;
                        }}
                      />
                      <div className="border rounded p-3 bg-light">
                        <small className="text-muted d-block mb-2">Preview</small>
                        <div dangerouslySetInnerHTML={{ __html: renderMarkdown(value || '') }} />
                      </div>
                    </div>
                  ) : field.type === 'image' ? (
                    <div className="d-flex flex-column gap-2">
                      {value ? (
                        <img
                          src={value}
                          alt="Preview"
                          className="rounded border"
                          style={{ width: '100%', maxHeight: 180, objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="rounded border bg-light py-4 text-center text-muted text-sm">No image</div>
                      )}
                      <input
                        className="form-control"
                        type="text"
                        value={value}
                        onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        placeholder="Paste image URL"
                      />
                      <input
                        className="form-control"
                        type="file"
                        accept="image/*"
                        disabled={!uploadHandler}
                        onChange={(e) => handleUpload(field.name, e.target.files?.[0])}
                      />
                      {uploading[field.name] && <small className="text-muted">Uploading...</small>}
                    </div>
                  ) : field.type === 'select' ? (
                    <select
                      className="form-select"
                      value={value}
                      onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    >
                      <option value="">Select</option>
                      {(options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === 'checkbox' ? (
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={Boolean(value)}
                        onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.checked }))}
                        id={`field-${field.name}`}
                      />
                      <label className="form-check-label" htmlFor={`field-${field.name}`}>
                        {field.helper || 'Enabled'}
                      </label>
                    </div>
                  ) : (
                    <input
                      className="form-control"
                      type={field.type || 'text'}
                      value={value}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, [field.name]: normalizeValue(field.type, e.target.value) }))
                      }
                      placeholder={field.placeholder}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CabCrudPage;
